import * as React from 'react';
import { useState } from 'react';

import type { Locale } from 'date-fns';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';

import { cn } from '../lib/utils';

import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_FROM_YEAR = 1900;

export function DatePickerWithYear({
  date,
  buttonClassName,
  placeholderText = 'Pick a date',
  locale,
  fromYear = DEFAULT_FROM_YEAR,
  toYear = CURRENT_YEAR,
  setDate,
}: {
  date?: Date;
  buttonClassName?: string;
  placeholderText?: string;
  locale?: Locale;
  fromYear?: number;
  toYear?: number;
  setDate: (date: Date, source: 'CALENDAR') => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'vai-w-full vai-justify-start vai-text-left vai-font-normal vai-min-w-40',
            !date && 'vai-text-muted-foreground',
            buttonClassName,
          )}
        >
          <CalendarIcon className="vai-mr-2 vai-h-4 vai-w-4" />
          <span>
            {date ? format(date, 'PPP', { locale }) : placeholderText}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="vai-w-auto vai-p-0">
        <Calendar
          defaultMonth={date}
          mode="single"
          selected={date}
          captionLayout="dropdown"
          fromYear={fromYear}
          toYear={toYear}
          onSelect={(selectedDate) => {
            selectedDate && setDate(selectedDate, 'CALENDAR');
            setIsOpen(false);
          }}
          classNames={{
            caption:
              'vai-flex vai-justify-center vai-pt-1 vai-relative vai-items-center',
            caption_label:
              'vai-text-sm vai-font-medium vai-cursor-pointer',
            caption_dropdowns:
              'vai-flex vai-gap-4 vai-items-center',
            dropdown_month: 'vai-relative vai-inline-flex vai-items-center',
            dropdown_year: 'vai-relative vai-inline-flex vai-items-center',
            dropdown:
              'vai-absolute vai-inset-0 vai-w-full vai-opacity-0 vai-cursor-pointer vai-z-10',
            dropdown_icon: 'vai-ml-1 vai-h-3 vai-w-3 vai-opacity-50',
            vhidden: 'vai-sr-only',
          }}
          initialFocus
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
